(function () {
  'use strict';

  if (window.KKCart && window.KKCart.__serviceVersion) {
    return;
  }

  var SERVICE_VERSION = '1.0.0';
  var FAST_TRACK_PROPERTY = '_kk_fast_track';
  var DRAWER_SELECTOR = '[data-kk-cart-drawer]';
  var state = {
    cart: null,
    sections: null,
    root: null,
    sectionId: '',
    requestSequence: 0,
    refreshController: null,
    refreshPromise: null,
    refreshRendersSections: false,
    mutationQueue: Promise.resolve(),
    pendingMutations: 0,
    pendingBusyMutations: 0,
    headerObserver: null,
    observedRefreshTimer: null,
    emittingLegacyEvent: false,
    initialized: false
  };

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function getDrawerRoot() {
    if (state.root && document.documentElement.contains(state.root)) {
      return state.root;
    }

    state.root = document.querySelector(DRAWER_SELECTOR);
    state.sectionId = state.root
      ? String(state.root.getAttribute('data-section-id') || '')
      : '';

    return state.root;
  }

  function normaliseRoot(value) {
    var root = String(value || '/');
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function shopifyRoot() {
    var drawer = getDrawerRoot();
    var configured = drawer && drawer.getAttribute('data-cart-root');
    var shopify = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return normaliseRoot(configured || shopify || '/');
  }

  function configuredRoute(datasetKey, fallback) {
    var drawer = getDrawerRoot();
    var configured = drawer && drawer.dataset ? drawer.dataset[datasetKey] : '';

    var route = configured || (shopifyRoot() + fallback.replace(/^\//, ''));
    var parts = String(route).split('?');

    if (!/\.js$/i.test(parts[0])) {
      parts[0] = parts[0].replace(/\/$/, '') + '.js';
    }

    return parts.join('?');
  }

  function routes() {
    return {
      cart: configuredRoute('cartUrl', 'cart.js'),
      add: configuredRoute('cartAddUrl', 'cart/add.js'),
      change: configuredRoute('cartChangeUrl', 'cart/change.js'),
      update: configuredRoute('cartUpdateUrl', 'cart/update.js'),
      clear: configuredRoute('cartClearUrl', 'cart/clear.js')
    };
  }

  function currentSectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  function getSectionId() {
    getDrawerRoot();
    return state.sectionId;
  }

  function sectionRequestFields() {
    var sectionId = getSectionId();
    return sectionId
      ? { sections: sectionId, sections_url: currentSectionsUrl() }
      : {};
  }

  function parseBoolean(value) {
    return value === true || value === 'true' || value === '1';
  }

  function getConfiguredFastTrackVariantIds() {
    var ids = [];

    toArray(document.querySelectorAll(DRAWER_SELECTOR + ', [data-kk-fast-track]')).forEach(function (element) {
      var value = element.getAttribute('data-fast-track-variant-id');
      if (value) {
        ids.push(String(value));
      }
    });

    return ids;
  }

  function isFastTrack(item) {
    if (!item) {
      return false;
    }

    var properties = item.properties || {};
    var propertyValue = properties[FAST_TRACK_PROPERTY];

    // Shopify can preserve a private line-item property with an empty or
    // false-like value. Only the explicit marker written by this theme means
    // that the line is the hidden Fast Track product. This mirrors the Liquid
    // checks used by the server-rendered drawer and header count.
    if (propertyValue === true || String(propertyValue || '').trim().toLowerCase() === 'true') {
      return true;
    }

    return getConfiguredFastTrackVariantIds().indexOf(String(item.variant_id || item.id || '')) !== -1;
  }

  function visibleItemCount(cart) {
    return (cart && cart.items ? cart.items : []).reduce(function (sum, item) {
      return isFastTrack(item) ? sum : sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function eligibleSubtotal(cart) {
    return (cart && cart.items ? cart.items : []).reduce(function (sum, item) {
      var linePrice = item.final_line_price;
      if (!Number.isFinite(Number(linePrice))) {
        linePrice = item.line_price;
      }

      return sum + (Number(linePrice) || 0);
    }, 0);
  }

  function decorateCart(cart) {
    if (!cart || typeof cart !== 'object') {
      return cart;
    }

    cart.visible_item_count = visibleItemCount(cart);
    cart.eligible_subtotal = eligibleSubtotal(cart);
    return cart;
  }

  function eventCart(cart) {
    var visibleCount = visibleItemCount(cart);
    var copy = Object.assign({}, cart || {});
    copy.item_count = visibleCount;
    copy.visible_item_count = visibleCount;
    copy.eligible_subtotal = eligibleSubtotal(cart);
    return copy;
  }

  function updateHeaderCount(cart) {
    var count = visibleItemCount(cart);

    toArray(document.querySelectorAll('[data-header-cart-count]')).forEach(function (element) {
      element.setAttribute('data-header-cart-count', count > 0 ? String(count) : '');
      element.classList.toggle('visible', count > 0);
      element.setAttribute('aria-label', String(count));
    });

    toArray(document.querySelectorAll('[data-header-cart-button], .site-header-cart-button')).forEach(function (button) {
      button.classList.toggle('count-hidden', count < 1);
    });

    toArray(document.querySelectorAll('[data-kk-cart-visible-count]')).forEach(function (element) {
      element.textContent = String(count);
      element.setAttribute('data-count', String(count));
    });
  }

  function dispatch(name, detail, target) {
    var event;

    try {
      event = new CustomEvent(name, { bubbles: true, detail: detail });
    } catch (error) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, detail);
    }

    (target || document).dispatchEvent(event);
  }

  function dispatchCartUpdated(cart, sections, source) {
    var rawCart = decorateCart(cart);
    var compatibleCart = eventCart(rawCart);
    var detail = {
      cart: rawCart,
      response: compatibleCart,
      rawCart: rawCart,
      visibleItemCount: compatibleCart.item_count,
      eligibleSubtotal: rawCart ? rawCart.eligible_subtotal : 0,
      sections: sections || null,
      sectionId: getSectionId(),
      source: source || 'refresh',
      kkCart: true
    };

    updateHeaderCount(rawCart);
    dispatch('kk:cart:updated', detail, document);
    dispatch('cart:updated', detail, document);

    try {
      if (window.api && window.api.theme && typeof window.api.theme.triggerEvent === 'function') {
        state.emittingLegacyEvent = true;
        window.api.theme.triggerEvent('cart:updated', {
          cancel: function () {},
          response: compatibleCart,
          kkCart: true
        });
      }
    } catch (error) {
      // DOM events and direct rendering remain authoritative.
    } finally {
      state.emittingLegacyEvent = false;
    }

    if (window.jQuery) {
      try {
        window.jQuery('[data-section-type="static-header"], .site-header').trigger(
          'cartcount:update',
          { response: compatibleCart, kkCart: true }
        );
      } catch (error) {
        // Direct DOM updates above remain authoritative.
      }
    }
  }

  function dispatchBusy(isBusy, source) {
    dispatch('kk:cart:busy', {
      busy: !!isBusy,
      source: source || 'cart',
      pendingMutations: state.pendingMutations
    });
  }

  function parseResponse(response) {
    var contentType = response.headers.get('content-type') || '';
    // Shopify Ajax Cart endpoints commonly return JSON with
    // `text/javascript; charset=utf-8` instead of `application/json`.
    // Treat both JSON and JavaScript MIME types as structured responses so
    // cart state never degrades into a JSON string.
    var reader = /json|javascript/i.test(contentType)
      ? response.json.bind(response)
      : response.text.bind(response);

    return reader().then(function (body) {
      if (response.ok) {
        return body;
      }

      var message = typeof body === 'string'
        ? body
        : body && (body.description || body.message || body.status);
      var error = new Error(message || 'Ostoskoria ei voitu päivittää.');
      error.status = response.status;
      error.response = body;
      throw error;
    });
  }

  function fetchJSON(url, options) {
    var requestOptions = Object.assign({
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }, options || {});

    return fetch(url, requestOptions).then(parseResponse);
  }

  function abortRefresh() {
    state.requestSequence += 1;

    if (state.refreshController) {
      state.refreshController.abort();
      state.refreshController = null;
    }
    state.refreshPromise = null;
    state.refreshRendersSections = false;
  }

  function drawerItemsViewURL() {
    var url = new URL(routes().cart, window.location.origin);
    url.pathname = url.pathname.replace(/\.js$/i, '');
    url.search = '';
    url.searchParams.set('view', 'kk-drawer-items');
    return url.toString();
  }

  function fetchDrawerItemsView(signal) {
    return fetch(drawerItemsViewURL(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
      signal: signal
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Ostoskorin sisältöä ei voitu näyttää.');
        }
        return response.text();
      })
      .then(function (html) {
        if (
          typeof html !== 'string' ||
          html.indexOf('data-kk-cart-drawer') === -1 ||
          html.indexOf('data-kk-cart-fallback-render') === -1
        ) {
          return null;
        }

        return { 'kk-cart-drawer-fallback': html };
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          throw error;
        }
        return null;
      });
  }

  function fetchRenderedSection(signal) {
    var sectionId = getSectionId();

    if (!sectionId) {
      return Promise.resolve(null);
    }

    var url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    return fetchJSON(url.toString(), {
      method: 'GET',
      cache: 'no-store',
      signal: signal
    })
      .then(function (sections) {
        return hasUsableDrawerSection(sections)
          ? sections
          : fetchDrawerItemsView(signal);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          throw error;
        }
        return fetchDrawerItemsView(signal);
      });
  }

  function refresh(options) {
    var settings = options || {};

    if (
      state.refreshPromise &&
      settings.force !== true &&
      (settings.render !== true || state.refreshRendersSections)
    ) {
      return state.refreshPromise;
    }

    var sequence = ++state.requestSequence;

    if (state.refreshController) {
      state.refreshController.abort();
    }

    state.refreshController = typeof AbortController === 'function'
      ? new AbortController()
      : null;

    var signal = state.refreshController ? state.refreshController.signal : undefined;
    var cartRequest = fetchJSON(routes().cart, {
      method: 'GET',
      cache: 'no-store',
      signal: signal
    });
    var sectionRequest = settings.render === false
      ? Promise.resolve(null)
      : fetchRenderedSection(signal);

    dispatchBusy(true, settings.source || 'refresh');

    var refreshPromise = Promise.all([cartRequest, sectionRequest])
      .then(function (results) {
        if (sequence !== state.requestSequence) {
          return state.cart;
        }

        state.cart = decorateCart(results[0]);
        state.sections = results[1];
        dispatchCartUpdated(state.cart, state.sections, settings.source || 'refresh');
        return state.cart;
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          return state.cart;
        }

        dispatch('kk:cart:error', {
          error: error,
          message: error && error.message,
          source: settings.source || 'refresh'
        });
        throw error;
      })
      .finally(function () {
        if (sequence === state.requestSequence) {
          state.refreshController = null;
          dispatchBusy(false, settings.source || 'refresh');
        }
        if (state.refreshPromise === refreshPromise) {
          state.refreshPromise = null;
          state.refreshRendersSections = false;
        }
      });

    state.refreshPromise = refreshPromise;
    state.refreshRendersSections = settings.render !== false;
    return refreshPromise;
  }

  function get(options) {
    var settings = options || {};

    if (state.cart && settings.force !== true) {
      return Promise.resolve(state.cart);
    }

    if (state.refreshPromise) {
      return state.refreshPromise;
    }

    return refresh({ render: settings.render === true, source: settings.source || 'get' });
  }

  function jsonMutation(url, payload, options) {
    var settings = options || {};
    var body = Object.assign({}, payload || {});
    if (settings.render !== false) {
      Object.assign(body, sectionRequestFields());
    }

    return fetchJSON(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  function formMutation(url, formData, options) {
    var settings = options || {};
    var body = new FormData();

    formData.forEach(function (value, key) {
      body.append(key, value);
    });

    if (settings.render !== false) {
      var fields = sectionRequestFields();
      Object.keys(fields).forEach(function (key) {
        if (!body.has(key)) {
          body.append(key, fields[key]);
        }
      });
    }

    return fetchJSON(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    });
  }

  function looksLikeCart(response) {
    return !!(
      response &&
      Array.isArray(response.items) &&
      typeof response.item_count === 'number' &&
      typeof response.total_price === 'number'
    );
  }

  function hasUsableDrawerSection(sections) {
    if (!sections || typeof sections !== 'object') {
      return false;
    }

    return Object.keys(sections).some(function (key) {
      return typeof sections[key] === 'string' && sections[key].indexOf('data-kk-cart-drawer') !== -1;
    });
  }

  function hasExplicitNullDrawerSection(sections) {
    var sectionId = getSectionId();
    return !!(
      sectionId &&
      sections &&
      typeof sections === 'object' &&
      Object.prototype.hasOwnProperty.call(sections, sectionId) &&
      sections[sectionId] === null
    );
  }

  function cartAfterMutation(response) {
    if (looksLikeCart(response)) {
      return Promise.resolve(response);
    }

    return fetchJSON(routes().cart, { method: 'GET', cache: 'no-store' });
  }

  function runMutation(source, request, options) {
    var settings = options || {};
    var silentBusy = settings.silentBusy === true;
    abortRefresh();
    state.pendingMutations += 1;
    if (!silentBusy) {
      state.pendingBusyMutations += 1;
      dispatchBusy(true, source);
    }

    return Promise.resolve().then(request)
      .then(function (response) {
        var sections = settings.render === false
          ? null
          : (response && response.sections ? response.sections : null);
        var sectionRequest = Promise.resolve(sections);
        if (settings.render !== false && getSectionId() && !hasUsableDrawerSection(sections)) {
          sectionRequest = hasExplicitNullDrawerSection(sections)
            ? fetchDrawerItemsView()
            : fetchRenderedSection();
        }

        return Promise.all([cartAfterMutation(response), sectionRequest]).then(function (results) {
          var cart = results[0];
          var renderedSections = hasUsableDrawerSection(results[1]) ? results[1] : sections;
          state.cart = decorateCart(cart);
          state.sections = renderedSections;
          dispatchCartUpdated(state.cart, renderedSections, source);
          return state.cart;
        });
      })
      .catch(function (error) {
        dispatch('kk:cart:error', {
          error: error,
          message: error && error.message,
          source: source
        });
        throw error;
      })
      .finally(function () {
        state.pendingMutations = Math.max(0, state.pendingMutations - 1);
        if (!silentBusy) {
          state.pendingBusyMutations = Math.max(0, state.pendingBusyMutations - 1);
          dispatchBusy(state.pendingBusyMutations > 0, source);
        }
      });
  }

  function enqueueMutation(source, request, options) {
    var task = function () {
      return runMutation(source, request, options);
    };
    var result = state.mutationQueue.then(task, task);

    state.mutationQueue = result.catch(function () {});
    return result;
  }

  function addFormPayload(formData) {
    var item = {};
    var indexedItems = Object.create(null);
    var payload = {};
    var hasFiles = false;

    function setItemField(target, field, value) {
      if (value === '' && /^(selling_plan|parent_id|parent_line_key)$/.test(field)) return;
      if (field === 'quantity') {
        if (value !== '') target.quantity = Number(value);
      } else {
        target[field] = value;
      }
    }

    function setProperty(target, name, value) {
      if (!target.properties) target.properties = Object.create(null);
      target.properties[name] = value;
    }

    formData.forEach(function (value, key) {
      if (typeof value !== 'string') {
        // Empty file inputs are not uploads. Real uploads retain the existing
        // multipart path; product forms with files bypass this service anyway.
        if (value && (value.size || value.name)) hasFiles = true;
        return;
      }
      var field = /^(id|quantity|selling_plan|parent_id|parent_line_key)$/.exec(key);
      var property = /^properties\[(.*)\]$/.exec(key);
      var indexedField = /^items\[(\d+)\]\[(id|quantity|selling_plan|parent_id|parent_line_key)\]$/.exec(key);
      var indexedProperty = /^items\[(\d+)\]\[properties\]\[(.*)\]$/.exec(key);
      var indexed = indexedField || indexedProperty;
      if (indexed) {
        var index = indexed[1];
        if (!indexedItems[index]) indexedItems[index] = {};
        if (indexedField) setItemField(indexedItems[index], indexedField[2], value);
        else setProperty(indexedItems[index], indexedProperty[2], value);
      } else if (field) {
        setItemField(item, field[1], value);
      } else if (property) {
        setProperty(item, property[1], value);
      } else if (/^(sections|sections_url|return_to|form_type|utf8)$/.test(key)) {
        payload[key] = value;
      }
    });

    if (hasFiles) return formData;
    payload.items = Object.keys(indexedItems).length
      ? Object.keys(indexedItems).sort(function (a, b) { return Number(a) - Number(b); })
        .map(function (index) { return indexedItems[index]; })
      : [item];
    return payload;
  }

  function normaliseAddPayload(input) {
    if (input instanceof FormData) {
      // Use the same items-array request as quick-buy and drawer upsells.
      // Flat multipart adds can bypass cumulative stock limits in this store.
      return addFormPayload(input);
    }

    if (Array.isArray(input)) {
      return { items: input };
    }

    if (input && Array.isArray(input.items)) {
      return input;
    }

    if (input && input.id) {
      return { items: [input] };
    }

    return input || {};
  }

  function reconcileRejectedAdd(error, settings, source) {
    if (!error || error.status !== 422) return Promise.reject(error);
    // Shopify may add the available remainder before returning 422. Reflect
    // the actual cart, but never retry a POST that might have partly succeeded.
    return Promise.all([
      fetchJSON(routes().cart, { method: 'GET', cache: 'no-store' }),
      settings.render === false ? Promise.resolve(null) : fetchRenderedSection()
    ]).then(function (results) {
      if (looksLikeCart(results[0])) {
        state.cart = decorateCart(results[0]);
        state.sections = results[1];
        dispatchCartUpdated(state.cart, state.sections, source);
      }
    }).catch(function () {
      // Keep the original add error when the reconciliation read also fails.
    }).then(function () { throw error; });
  }

  function add(input, options) {
    var settings = options || {};
    var payload = normaliseAddPayload(input);
    var source = settings.source || 'add';

    return enqueueMutation(source, function () {
      function submit() {
        var request = payload instanceof FormData
          ? formMutation(routes().add, payload, settings)
          : jsonMutation(routes().add, payload, settings);
        return request.catch(function (error) {
          return reconcileRejectedAdd(error, settings, source);
        });
      }

      if (!settings.ifProductMissing) {
        return submit();
      }

      // Reconcile a one-per-product recommendation within the mutation queue.
      // This read must not share refresh()'s abort/stale-cache fallback: a lost
      // POST response followed by a retry must not add the product twice.
      return fetchJSON(routes().cart, { method: 'GET', cache: 'no-store' }).then(function (cart) {
        if (!looksLikeCart(cart)) {
          throw new Error('Invalid cart response');
        }
        var alreadyAdded = cart.items.some(function (item) {
          return String(item.product_id) === String(settings.ifProductMissing);
        });
        return alreadyAdded ? cart : submit();
      });
    }, settings);
  }

  function change(id, quantity, options) {
    var settings = options || {};
    var payload = typeof id === 'object'
      ? Object.assign({}, id)
      : { id: id, quantity: quantity };

    return enqueueMutation(settings.source || 'change', function () {
      return jsonMutation(routes().change, payload, settings);
    }, settings);
  }

  function update(payload, options) {
    var settings = options || {};

    return enqueueMutation(settings.source || 'update', function () {
      return jsonMutation(routes().update, payload || {}, settings);
    }, settings);
  }

  function clear(options) {
    var settings = options || {};

    return enqueueMutation(settings.source || 'clear', function () {
      return jsonMutation(routes().clear, {}, settings);
    }, settings);
  }

  function drawerMatchesCart(cart) {
    var drawer = getDrawerRoot();
    if (!drawer || !cart || !Array.isArray(cart.items)) {
      return false;
    }

    var expectedLines = cart.items
      .filter(function (item) { return !isFastTrack(item); })
      .map(function (item) {
        var key = String(item.key || '');
        return key ? key + '\u0000' + String(Number(item.quantity) || 0) : '';
      })
      .filter(Boolean)
      .sort();
    var renderedLines = toArray(drawer.querySelectorAll('[data-kk-cart-item][data-line-key]'))
      .map(function (item) {
        var input = item.querySelector('[data-kk-cart-quantity]');
        var key = String(item.getAttribute('data-line-key') || '');
        return key ? key + '\u0000' + String(input ? Number(input.value) || 0 : 0) : '';
      })
      .filter(Boolean)
      .sort();

    return expectedLines.length === renderedLines.length && expectedLines.every(function (line, index) {
      return line === renderedLines[index];
    });
  }

  function open(options) {
    var detail = Object.assign({ source: 'api' }, options || {});

    return ensureDrawerController(detail.source || 'api')
      .then(function () {
        return whenIdle();
      })
      .then(function (cart) {
        return cart || get({ render: true, source: 'open' });
      })
      .then(function (cart) {
        if (!getDrawerRoot()) {
          throw new Error('Ostoskorin sivupaneelia ei löytynyt.');
        }

        if (drawerMatchesCart(cart)) {
          return cart;
        }

        return refresh({ render: true, force: true, source: 'open-repair' })
          .then(function (freshCart) {
            return freshCart || cart;
          });
      })
      .then(function (cart) {
        if (!getDrawerRoot()) {
          throw new Error('Ostoskorin sivupaneelia ei löytynyt.');
        }

        if (!drawerMatchesCart(cart)) {
          // A null/stale Section Rendering response must not turn a working
          // drawer into a page-cart redirect. The drawer controller also has
          // a JSON fallback and can reconcile the visible rows after opening.
          dispatch('kk:cart:render-mismatch', {
            cart: cart,
            sectionId: getSectionId(),
            source: detail.source || 'api'
          });
        }

        dispatch('kk:cart:open', detail);
        return cart;
      });
  }

  function close(options) {
    dispatch('kk:cart:close', Object.assign({ source: 'api' }, options || {}));
  }

  function whenIdle() {
    return state.mutationQueue.then(function () {
      return state.cart;
    });
  }

  function ensureDrawerController(source) {
    if (
      !isDrawerMode() ||
      !window.KKCartDrawerLoader ||
      typeof window.KKCartDrawerLoader.load !== 'function'
    ) {
      return Promise.resolve();
    }

    return window.KKCartDrawerLoader.load({
      source: source || 'cart-service'
    });
  }

  function handleExternalUpdate(cart, options) {
    var settings = options || {};
    var source = settings.source || 'external-add';

    abortRefresh();
    if (cart && typeof cart === 'object') {
      state.cart = decorateCart(cart);
      state.sections = null;
      updateHeaderCount(state.cart);
    }

    // External add handlers can run before the lazy drawer controller exists.
    // When opening, bind its render listeners before publishing the new rows.
    var drawerReady = settings.open ? ensureDrawerController(source) : Promise.resolve();
    return drawerReady
      .then(function () {
        return refresh({ render: true, force: true, source: source });
      })
      .then(function (freshCart) {
        if (!settings.open || !isDrawerMode()) {
          return freshCart;
        }

        // Use the normal open path so stale/missing rows are reconciled too.
        return open({
          source: source,
          trigger: settings.trigger || null
        });
      });
  }

  function isDrawerMode() {
    var drawer = getDrawerRoot();
    if (!drawer) {
      return false;
    }

    var mode = drawer.getAttribute('data-cart-mode');
    return !mode || mode === 'drawer';
  }

  function cartPathname() {
    try {
      return new URL(routes().cart, window.location.origin).pathname.replace(/\.js$/, '');
    } catch (error) {
      return shopifyRoot() + 'cart';
    }
  }

  function isCartLink(link) {
    if (!link || !link.href) {
      return false;
    }

    try {
      var pathname = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
      return pathname === cartPathname().replace(/\/$/, '');
    } catch (error) {
      return false;
    }
  }

  function handleCartLinkClick(event) {
    if (!isDrawerMode() || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var link = event.target && event.target.closest
      ? event.target.closest('[data-kk-cart-open], a[href]')
      : null;

    if (!link || (!link.hasAttribute('data-kk-cart-open') && !isCartLink(link))) {
      return;
    }

    // Keep the explicit "view cart" fallback inside the drawer navigable.
    if (link.closest(DRAWER_SELECTOR) && !link.hasAttribute('data-kk-cart-open')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    open({ source: 'cart-link', trigger: link }).catch(function (error) {
      dispatch('kk:cart:error', {
        error: error,
        message: error && error.message,
        source: 'cart-link'
      });

      // Drawer mode must never fall through to the page cart because of a
      // transient render/network race. Retry once and keep the customer on the
      // current page even if the drawer needs a moment longer to become ready.
      window.setTimeout(function () {
        open({ source: 'cart-link-retry', trigger: link }).catch(function (retryError) {
          dispatch('kk:cart:error', {
            error: retryError,
            message: retryError && retryError.message,
            source: 'cart-link-retry'
          });
        });
      }, 250);
    });
  }

  function formHasFiles(form) {
    return toArray(form.querySelectorAll('input[type="file"]')).some(function (input) {
      return input.files && input.files.length > 0;
    });
  }

  function isAddForm(form) {
    if (
      !form ||
      form.matches('[data-kk-cart-bypass], [data-kk-mystery-set-form]') ||
      form.querySelector('[data-kk-mystery-set-atc]')
    ) {
      return false;
    }

    try {
      return /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, window.location.origin).pathname);
    } catch (error) {
      return false;
    }
  }

  function setFormBusy(form, submitter, busy) {
    var button = submitter || form.querySelector('[type="submit"], [name="add"]');

    form.classList.toggle('kk-cart-submitting', !!busy);
    form.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (button) {
      if (busy) {
        button.setAttribute('data-kk-cart-was-disabled', button.disabled ? 'true' : 'false');
        button.classList.add('processing');
        button.disabled = true;
      } else {
        button.classList.remove('processing');
        button.disabled = button.getAttribute('data-kk-cart-was-disabled') === 'true';
        button.removeAttribute('data-kk-cart-was-disabled');
      }
    }
  }

  function clearFormError(form) {
    var live = form.querySelector('[data-kk-cart-form-error]');
    if (live) live.remove();
  }

  function getFormSelection(form) {
    var field = form.querySelector('[name="id"]');
    var variantId = field ? String(field.value || '') : '';
    var previousId = form.getAttribute('data-kk-cart-form-variant-id');
    var revision = Number(form.getAttribute('data-kk-cart-form-variant-revision') || 0);

    // Shared with the early bridge so queued requests retain their selection
    // history when the main cart service takes over, including A -> B -> A.
    if (previousId !== null && previousId !== variantId) {
      revision += 1;
      clearFormError(form);
    }
    form.setAttribute('data-kk-cart-form-variant-id', variantId);
    form.setAttribute('data-kk-cart-form-variant-revision', String(revision));
    return { variantId: variantId, revision: revision };
  }

  function handleFormVariantChange(event) {
    var field = event.target;
    if (!field || !field.matches || !field.matches('[name="id"]')) return;
    var form = field.form || field.closest('form');
    if (isAddForm(form)) getFormSelection(form);
  }

  function reportFormError(form, error) {
    var live = form.querySelector('[data-kk-cart-form-error]');

    if (!live) {
      live = document.createElement('p');
      live.setAttribute('data-kk-cart-form-error', '');
      live.setAttribute('role', 'alert');
      live.className = 'kk-cart-form-error';
      form.appendChild(live);
    }

    live.textContent = error && error.message
      ? error.message
      : 'Tuotetta ei voitu lisätä ostoskoriin.';
  }

  function handleAddFormSubmit(event) {
    var form = event.target;
    var submitter = event.submitter || null;

    if (
      !isDrawerMode() ||
      event.defaultPrevented ||
      !isAddForm(form) ||
      formHasFiles(form) ||
      (submitter && submitter.matches('[name="checkout"], .shopify-payment-button__button'))
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.classList.contains('kk-cart-submitting')) return;
    var submittedSelection = getFormSelection(form);
    clearFormError(form);
    setFormBusy(form, submitter, true);

    add(new FormData(form), { source: 'product-form' })
      .then(function () {
        return open({ source: 'product-form', trigger: submitter });
      })
      .catch(function (error) {
        var currentSelection = getFormSelection(form);
        if (
          form.isConnected &&
          currentSelection.variantId === submittedSelection.variantId &&
          currentSelection.revision === submittedSelection.revision
        ) {
          reportFormError(form, error);
        }
      })
      .finally(function () {
        setFormBusy(form, submitter, false);
      });
  }

  function handleQuickBuyClick(event) {
    if (
      !isDrawerMode() ||
      event.defaultPrevented ||
      event.button > 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    var button = event.target && event.target.closest
      ? event.target.closest('[data-quick-buy][data-variant-id]')
      : null;

    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') {
      return;
    }

    var variantId = String(button.getAttribute('data-variant-id') || '').trim();
    if (!variantId) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    button.classList.add('processing');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    add({
      items: [{
        id: variantId,
        quantity: 1
      }]
    }, { source: 'quick-buy' })
      .then(function () {
        return open({ source: 'quick-buy', trigger: button });
      })
      .catch(function (error) {
        var card = button.closest('[data-product-item]');
        var live = card && card.querySelector('[data-kk-cart-form-error]');
        if (!live && card) {
          live = document.createElement('p');
          live.setAttribute('data-kk-cart-form-error', '');
          live.setAttribute('role', 'alert');
          live.className = 'kk-cart-form-error';
          card.appendChild(live);
        }
        if (live) {
          live.textContent = error && error.message
            ? error.message
            : 'Tuotetta ei voitu lisätä ostoskoriin.';
        }
      })
      .finally(function () {
        button.classList.remove('processing');
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  function scheduleObservedRefresh(source) {
    if (state.observedRefreshTimer) {
      window.clearTimeout(state.observedRefreshTimer);
    }

    state.observedRefreshTimer = window.setTimeout(function () {
      state.observedRefreshTimer = null;
      if (state.pendingMutations > 0) {
        return;
      }
      refresh({ render: isDrawerMode(), source: source || 'observed-cart-update' }).catch(function () {});
    }, 80);
  }

  function handleObservedCartEvent(event) {
    if (state.emittingLegacyEvent || (event && event.detail && event.detail.kkCart)) {
      return;
    }

    scheduleObservedRefresh('legacy-cart-event');
  }

  function observeHeaderCount() {
    if (state.headerObserver || typeof MutationObserver !== 'function') {
      return;
    }

    state.headerObserver = new MutationObserver(function (mutations) {
      if (!state.cart || state.pendingMutations > 0) {
        return;
      }

      var expected = visibleItemCount(state.cart);
      var changedUnexpectedly = mutations.some(function (mutation) {
        var raw = mutation.target.getAttribute('data-header-cart-count');
        var observed = parseInt(raw || '0', 10) || 0;
        return observed !== expected;
      });

      if (changedUnexpectedly) {
        scheduleObservedRefresh('header-count-observer');
      }
    });

    state.headerObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-header-cart-count'],
      subtree: true
    });
  }

  function initialise() {
    if (state.initialized) {
      return;
    }

    state.initialized = true;
    getDrawerRoot();
    document.addEventListener('click', handleCartLinkClick, true);
    document.addEventListener('click', handleQuickBuyClick, true);
    document.addEventListener('submit', handleAddFormSubmit, true);
    document.addEventListener('change', handleFormVariantChange, true);
    document.addEventListener('cart:updated', handleObservedCartEvent);
    window.addEventListener('cart:updated', handleObservedCartEvent);
    document.addEventListener('cart:refresh', handleObservedCartEvent);
    window.addEventListener('cart:refresh', handleObservedCartEvent);
    observeHeaderCount();

    if (isDrawerMode()) {
      get({ source: 'initial', render: false }).catch(function () {});
    }
  }

  window.KKCart = {
    __serviceVersion: SERVICE_VERSION,
    FAST_TRACK_PROPERTY: FAST_TRACK_PROPERTY,
    state: state,
    configure: function (root) {
      state.root = root || document.querySelector(DRAWER_SELECTOR);
      state.sectionId = state.root
        ? String(state.root.getAttribute('data-section-id') || '')
        : '';
      return this;
    },
    routes: routes,
    getSectionId: getSectionId,
    get: get,
    refresh: refresh,
    add: add,
    change: change,
    update: update,
    clear: clear,
    open: open,
    close: close,
    whenIdle: whenIdle,
    handleExternalUpdate: handleExternalUpdate,
    isFastTrack: isFastTrack,
    visibleItemCount: visibleItemCount,
    eligibleSubtotal: eligibleSubtotal,
    updateHeaderCount: updateHeaderCount,
    isDrawerMode: isDrawerMode
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
}());
